import { saveAs } from 'file-saver';

import {
  BranchDetailResult,
  FileDetailResult,
  FinancialDetailResult,
  GeneralDetailResult,
  OwnershipDetailResult,
  OwnershipGraphResult,
  RatioDetailResult,
} from '../../generated/model/models';
import { RawFinancialDetailResult } from '../../generated/model/rawFinancialDetailResult';
import { httpClient } from '../config/httpClient';
import { parseFilename } from '../utils/axios.utils';
import { makeUrl } from '../utils/routes.utils';

const path = {
  generalData: '/company/detail/{id}/general?recaptchaToken={recaptchaToken}',
  financialsData:
    '/company/detail/{id}/finance?recaptchaToken={recaptchaToken}&searchId={searchId}',
  rawFinancialsData: '/company/detail/{id}/raw-finance?recaptchaToken={recaptchaToken}',
  ratiosData: '/company/detail/{id}/ratio?recaptchaToken={recaptchaToken}&searchId={searchId}',
  ownershipData: '/company/detail/{id}/ownership?recaptchaToken={recaptchaToken}',
  branchesData: '/company/detail/{id}/branch?recaptchaToken={recaptchaToken}',
  documentsData: '/company/detail/{id}/file?recaptchaToken={recaptchaToken}',
  companyConnections: '/company/network/{id}?recaptchaToken={recaptchaToken}',
  downloadFiles: '/company/file/{id}/{internalId}?recaptchaToken={recaptchaToken}',
  ownershipConnections: '/company/detail/{id}/ownership-graph?recaptchaToken={recaptchaToken}',
};

const withDatasetInfo = (path: string, dataset?: string, dbVersion?: string) => {
  let updatedPath = path;

  if (dataset) {
    updatedPath += `&dataset=${encodeURIComponent(dataset)}`;
  }

  if (dbVersion) {
    updatedPath += `&dbVersion=${encodeURIComponent(dbVersion)}`;
  }

  return updatedPath;
};

export const getGeneralData = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<GeneralDetailResult> => {
  const response = await httpClient.get<GeneralDetailResult>(
    withDatasetInfo(makeUrl(path.generalData, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data;
};

export const getFinancialsData = async (
  id: string,
  searchId: string | null,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<FinancialDetailResult> => {
  const response = await httpClient.get<FinancialDetailResult>(
    withDatasetInfo(
      makeUrl(path.financialsData, { id, recaptchaToken, searchId }),
      dataset,
      dbVersion,
    ),
  );
  return response.data;
};

export const getRawFinancialsData = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<RawFinancialDetailResult> => {
  const response = await httpClient.get<RawFinancialDetailResult>(
    withDatasetInfo(makeUrl(path.rawFinancialsData, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data;
};

export const getRatiosData = async (
  id: string,
  searchId: string | null,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<RatioDetailResult> => {
  const response = await httpClient.get<RatioDetailResult>(
    withDatasetInfo(makeUrl(path.ratiosData, { id, recaptchaToken, searchId }), dataset, dbVersion),
  );
  return response.data;
};

export const getOwnershipData = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<OwnershipDetailResult> => {
  const response = await httpClient.get<OwnershipDetailResult>(
    withDatasetInfo(makeUrl(path.ownershipData, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data;
};

export const getBranchesData = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<BranchDetailResult> => {
  const response = await httpClient.get<BranchDetailResult>(
    withDatasetInfo(makeUrl(path.branchesData, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data;
};

export const getCompanyConnections = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<any> => {
  const response = await httpClient.get(
    withDatasetInfo(makeUrl(path.companyConnections, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data.nodes;
};

export const getOwnershipConnections = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<OwnershipGraphResult> => {
  const response = await httpClient.get<OwnershipGraphResult>(
    withDatasetInfo(makeUrl(path.ownershipConnections, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data;
};

export const getDocumentsData = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
): Promise<FileDetailResult> => {
  const response = await httpClient.get<FileDetailResult>(
    withDatasetInfo(makeUrl(path.documentsData, { id, recaptchaToken }), dataset, dbVersion),
  );
  return response.data;
};

export const getDocument = async (
  id: string,
  recaptchaToken: string,
  dataset?: string,
  dbVersion?: string,
  internalId?: string,
  ids?: string[],
) => {
  const endpoint = withDatasetInfo(
    makeUrl(path.downloadFiles, { id, recaptchaToken, internalId }),
    dataset,
    dbVersion,
  );

  let response: any = {};

  if (internalId) {
    response = await httpClient.get<Blob>(endpoint, {
      responseType: 'blob',
    });
  } else {
    response = await httpClient.post<Blob>(endpoint, ids, {
      responseType: 'blob',
    });
  }

  const filename = parseFilename(response.headers['content-disposition']);

  const blob = new Blob([response.data], { type: response.data.type });

  saveAs(blob, filename);
};
